// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getPartnersList, selectQuery } from '../../feature';

// Components
import { Input, Select } from '@/ui';
import { StyledDatePicker, StyledFilterRow } from './PartnersFiltersContainerStyles';

// Helpers
import { GetPartnersListDTO } from '../../dtos';
import { getActiveOptions, getHiddenOptions } from '../../helpers/getOptions';
import { PartnersFiltersFields } from '../../helpers/types';

type IPartnersFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const PartnersFiltersContainer = ({ children, className }: IPartnersFiltersContainerProps) => {
  const { t } = useTranslation(['usersList']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetPartnersListDTO(searchParams);
        dispatch(getPartnersList(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleBooleanFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateChange = (name: string) => (date: Dayjs) => {
    const newQuery = { ...query, [name]: date ? dayjs(date).format('YYYY-MM-DD') : undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('users_list_title')}
            name={PartnersFiltersFields.TITLE}
            onChange={handleSearch}
            value={query?.title || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('users_store_title')}
            name={PartnersFiltersFields.STORE_TITLE}
            onChange={handleSearch}
            value={query?.store_title || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <StyledDatePicker onChange={handleDateChange(PartnersFiltersFields.CREATED_AT)} size="middle" />
        </th>
        <th>
          <StyledDatePicker onChange={handleDateChange(PartnersFiltersFields.LAST_LOGIN_AT)} size="middle" />
        </th>
        <th>
          <Input
            placeholder={t('users_list_phone_number')}
            name={PartnersFiltersFields.PHONE}
            onChange={handleSearch}
            value={query?.phone || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('users_list_email')}
            name={PartnersFiltersFields.EMAIL}
            onChange={handleSearch}
            value={query?.email || ''}
            size="large"
            allowClear
          />
        </th>
        <th />
        <th>
          <Select
            options={getActiveOptions(t)}
            placeholder={t('users_list_partner_status')}
            onChange={handleBooleanFilter(PartnersFiltersFields.ACTIVE)}
            value={query?.active}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getHiddenOptions(t)}
            placeholder={t('users_list_visibility')}
            onChange={handleBooleanFilter(PartnersFiltersFields.HIDDEN)}
            value={query?.hidden}
            size="large"
            allowClear
          />
        </th>
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PartnersFiltersContainer);
